
import { saveFilter, loadFilters, clearFilters as storageClear } from '../filter-manager.storage.js';
import { attachEventListeners } from './manager-events.js';
import { applyFiltersToUI } from './manager-ui.js';
import { isRegexSearch, matchesRegexPattern } from './manager-utils.js';
import { applyFiltersLogic } from './filter-logic.js';

import { cancelScheduledFallback } from './fuzzy-fallback.js';

export class FilterManager {
    constructor() {
        this.activeFilters = { category: null, subcategory: null, format: null, search: '' };
        this.filterElements = { categorySelect: null, subcategorySelect: null, formatFilter: null, searchInput: null };
        this.filterCallbacks = [];
        this.searchDebounceTimeout = null;
        this.searchDebounceDelay = 1500; // 1.5 seconds debounce for URL update
        this.fileListRefreshTimeout = null;
        this.fileListRefreshDelay = 150; // 150ms for fast file list update
        this._isApplyingFilters = false;
        this._applyFiltersTimeout = null;
        this._lastApplyFiltersCall = 0;
        this._lastApplyFiltersUpdateUrl = null; // Track updateUrl parameter for duplicate detection
    }

    init(elements) {
        this.filterElements = elements;
        this.attachEventListeners();
        this.loadSavedFilters();
    }

    attachEventListeners() { attachEventListeners(this); }

    handleCategoryChange() {
        const value = this.filterElements.categorySelect.value;
        this.activeFilters.category = value || null;
        this.updateSubcategoryOptions();
        saveFilter('category', value);
        // Don't update URL here - URL will be updated when search is executed (debounce)
        this.applyFilters(false); // false = don't update URL (filter change, not search execution)
    }

    handleSubcategoryChange() {
        const value = this.filterElements.subcategorySelect.value;
        this.activeFilters.subcategory = value || null;
        saveFilter('subcategory', value);
        // Don't update URL here - URL will be updated when search is executed (debounce)
        this.applyFilters(false); // false = don't update URL (filter change, not search execution)
    }

    handleFormatChange() {
        const value = this.filterElements.formatFilter.value;
        this.activeFilters.format = value || null;
        saveFilter('format', value);
        // Don't update URL here - URL will be updated when search is executed (debounce)
        this.applyFilters(false); // false = don't update URL (filter change, not search execution)
    }

    handleDateFromChange() {
        const dateFrom = this.filterElements.dateFromInput ? this.filterElements.dateFromInput.value : undefined;
        if (debugMode === '1') { console.log('[FilterManager] Date from changed:', dateFrom); }
        
        // Sync date filter to filterController for URL synchronization
        if (window.filterController && typeof window.filterController.activeFilters !== 'undefined') {
            window.filterController.activeFilters.dateFrom = (dateFrom || '').toString().trim();
        }
        
        // Force highlight name search count if on fulltext tab
        try {
            const fulltextTab = window.jQuery ? window.jQuery('#nav-full-text-card-tab') : null;
            if (fulltextTab && fulltextTab.length && fulltextTab.hasClass('active')) {
                if (window.FileList && typeof window.FileList.forceHighlightNameSearchCount === 'function') {
                    window.FileList.forceHighlightNameSearchCount();
                }
            }
        } catch(_) { /* noop */ }
        
        // Don't update URL here - URL will be updated when search is executed (debounce)
        this.applyFilters(false); // false = don't update URL (filter change, not search execution)
    }

    handleDateToChange() {
        const dateTo = this.filterElements.dateToInput ? this.filterElements.dateToInput.value : undefined;
        if (debugMode === '1') { console.log('[FilterManager] Date to changed:', dateTo); }
        
        // Sync date filter to filterController for URL synchronization
        if (window.filterController && typeof window.filterController.activeFilters !== 'undefined') {
            window.filterController.activeFilters.dateTo = (dateTo || '').toString().trim();
        }
        
        // Force highlight name search count if on fulltext tab
        try {
            const fulltextTab = window.jQuery ? window.jQuery('#nav-full-text-card-tab') : null;
            if (fulltextTab && fulltextTab.length && fulltextTab.hasClass('active')) {
                if (window.FileList && typeof window.FileList.forceHighlightNameSearchCount === 'function') {
                    window.FileList.forceHighlightNameSearchCount();
                }
            }
        } catch(_) { /* noop */ }
        
        // Don't update URL here - URL will be updated when search is executed (debounce)
        this.applyFilters(false); // false = don't update URL (filter change, not search execution)
    }

    handleSearchInput() {
        const searchValue = this.filterElements.searchInput ? this.filterElements.searchInput.value : undefined;
        const query = (searchValue || '').toString().trim();
        
        // Check if query was cleared (transition from non-empty to empty) BEFORE updating storage
        try {
            const lastQuery = sessionStorage.getItem('lazy_load_last_query') || '';
            const hadQueryBefore = lastQuery && typeof lastQuery === 'string' && lastQuery.trim().length > 0;
            const isEmptyNow = !query || (typeof query === 'string' && !query.trim());
            const queryWasCleared = hadQueryBefore && isEmptyNow;
            
            // If query was cleared, set flag for shuffle
            if (queryWasCleared) {
                sessionStorage.setItem('query_was_cleared', 'true');
            }
            
            // Update lazy_load_last_query to track search state for shuffle detection
            sessionStorage.setItem('lazy_load_last_query', query || '');
        } catch(e) {
            // Silent fail
        }
        
        // Sync search query to filterController for URL synchronization
        if (window.filterController && typeof window.filterController.activeFilters !== 'undefined') {
            window.filterController.activeFilters.searchQuery = query;
            window.QUERY = query;
        }
        
        // Force highlight name search count if on fulltext tab
        try {
            const fulltextTab = window.jQuery ? window.jQuery('#nav-full-text-card-tab') : null;
            if (fulltextTab && fulltextTab.length && fulltextTab.hasClass('active')) {
                if (window.FileList && typeof window.FileList.forceHighlightNameSearchCount === 'function') {
                    window.FileList.forceHighlightNameSearchCount();
                }
            }
        } catch(_) { /* noop */ }
        
        // Debounce the ENTIRE search so nothing runs until the user pauses (~350ms).
        // ⚠️ triggerDebouncedSearch() schedules applyFilters(true) at 0ms — i.e. it ran
        // the heavy fuzzy pass on EVERY keystroke, so debouncing applyFilters(false)
        // alone did NOTHING (real cause of per-keystroke lag on mobile). Fold the trigger
        // INTO the debounce so the search fires ONCE, after typing/deleting stops.
        // applyFilters(true) renders results AND schedules the URL update (own 1.5s debounce).
        clearTimeout(this._liveFilterTimeout);
        this._liveFilterTimeout = setTimeout(() => { this.triggerDebouncedSearch(); }, 350);
    }
    
    /**
     * Trigger debounced search execution - updates URL when search runs
     * This is called when filters change (categories, dates) or search input changes
     * Note: URL debounce (2 seconds) is handled in updateUrlWithFilters, so we call applyFilters immediately
     */
    triggerDebouncedSearch() {
        const debugMode = (new URLSearchParams(window.location.search)).get('debug') || '0';
        
        // Отменить запланированный fallback при новом поиске
        // Пользователь продолжает вводить - fallback не нужен
        cancelScheduledFallback(debugMode, 'new search triggered (user continued typing)');
        
        if (this.searchDebounceTimeout) {
            if (debugMode === '1') {
                console.log('[FilterManager] triggerDebouncedSearch: Cancelling previous timeout');
            }
            clearTimeout(this.searchDebounceTimeout);
        }
        // Apply filters immediately - URL update will be debounced in updateUrlWithFilters (1.5 seconds)
        // Use setTimeout with 0ms to ensure it runs after current call stack, but debounce is handled in updateUrlWithFilters
        if (debugMode === '1') {
            console.log('[FilterManager] triggerDebouncedSearch: Scheduling applyFilters(true)');
        }
        this.searchDebounceTimeout = setTimeout(() => { 
            if (debugMode === '1') {
                console.log('[FilterManager] triggerDebouncedSearch: Executing applyFilters(true)');
            }
            this.applyFilters(true); // true = update URL (search execution) - debounce handled in updateUrlWithFilters
        }, 0);
    }

    clearAllFilters() {
        if (debugMode === '1') { console.log('[FilterManager] Clearing all filters (preserving search query)'); }
        
        // Preserve search query - only clear filters, not search
        let currentSearchQuery = (this.filterElements.searchInput && this.filterElements.searchInput.value) || '';
        try {
            const $sb = window.jQuery ? window.jQuery('#fnd_searchbox') : null;
            if ($sb && typeof $sb.typeahead === 'function') {
                const taVal = $sb.typeahead('val');
                if (typeof taVal === 'string' && taVal.length >= 0) {
                    currentSearchQuery = taVal;
                }
            }
        } catch(_) {}
        
        // Preserve public only filter checkbox state - DO NOT clear it
        const publicOnlyCheckbox = document.getElementById('public-only-filter');
        const preservedPublicOnly = publicOnlyCheckbox && publicOnlyCheckbox.checked;
        
        // Clear only filter inputs (dates, categories, formats), NOT search input
        if (this.filterElements.dateFromInput) this.filterElements.dateFromInput.value = '';
        if (this.filterElements.dateToInput) this.filterElements.dateToInput.value = '';
        // DO NOT clear searchInput here - preserve search query
        if (this.filterElements.categorySelect) this.filterElements.categorySelect.value = '';
        if (this.filterElements.subcategorySelect) this.filterElements.subcategorySelect.value = '';
        if (this.filterElements.formatFilter) this.filterElements.formatFilter.value = '';
        // DO NOT clear publicOnlyCheckbox - preserve its state
        
        // Preserve search query and public only filter in activeFilters
        this.activeFilters = { 
            category: null, 
            subcategory: null, 
            format: null, 
            search: currentSearchQuery, // Preserve search query
            dateFrom: '', 
            dateTo: '',
            publicOnly: preservedPublicOnly // Preserve public only filter state
        };
        
        // Sync cleared filters to filterController for URL synchronization
        // Preserve searchQuery in filterController as well
        if (window.filterController && typeof window.filterController.activeFilters !== 'undefined') {
            // Preserve search query - only clear date filters
            const preservedSearchQuery = window.filterController.activeFilters.searchQuery || currentSearchQuery || '';
            window.filterController.activeFilters.searchQuery = preservedSearchQuery;
            window.filterController.activeFilters.dateFrom = '';
            window.filterController.activeFilters.dateTo = '';
            // Preserve window.QUERY
            if (preservedSearchQuery) {
                window.QUERY = preservedSearchQuery;
            }
        }
        
        // Don't update URL here - URL will be updated when search is executed (debounce)
        // Note: Query clearing is handled in handleSearchInput, not here
        this.applyFilters(false); // false = don't update URL (filter change, not search execution)
    }

    applyFilters(updateUrl = true) {
        const debugMode = (new URLSearchParams(window.location.search)).get('debug') || '0';
        
        // CRITICAL: Allow applyFilters(true) to proceed even if applyFilters(false) is in progress
        // This ensures URL updates are not blocked by filter application
        // Only block if BOTH calls have same updateUrl value
        if (this._isApplyingFilters && this._lastApplyFiltersUpdateUrl === updateUrl) {
            if (debugMode === '1') {
                console.log('[FilterManager] applyFilters skipped - already applying with same updateUrl:', updateUrl);
            }
            return;
        }
        
        // Check if this is a duplicate call (same parameters within 50ms)
        const now = Date.now();
        if (this._lastApplyFiltersCall && (now - this._lastApplyFiltersCall) < 50 && this._lastApplyFiltersUpdateUrl === updateUrl) {
            if (debugMode === '1') {
                console.log('[FilterManager] applyFilters skipped - duplicate call within 50ms with same updateUrl:', updateUrl);
            }
            return;
        }
        this._lastApplyFiltersCall = now;
        this._lastApplyFiltersUpdateUrl = updateUrl; // Track updateUrl for duplicate detection
        
        this._isApplyingFilters = true;
        try {
            applyFiltersLogic(this, updateUrl);
        } finally {
            // Reset flag after a short delay to allow render to complete
            setTimeout(() => {
                this._isApplyingFilters = false;
                this._lastApplyFiltersUpdateUrl = null;
            }, 100);
        }
    }

    /**
     * Continue paused search - called by LazyLoadManager when user scrolls down
     */
    continueSearch() {
        if (!window.searchStateManager || !window.searchStateManager._paused) {
            return;
        }

        // Get current conditions and files
        const conditions = this.getMatchConditions();
        if (!conditions || conditions.length === 0) {
            return;
        }

        const files = window.filesStore ? window.filesStore.getFiles() : [];
        if (!files || files.length === 0) {
            return;
        }

        if (debugMode === '1') {
            console.log('[SEARCH] Resuming...');
        }

        // Continue search with resume flag
        const searchResult = window.myEach(files, conditions, true, false, false, 50, true);
        
        if (searchResult && typeof searchResult === 'object' && searchResult.results) {
            const newResults = searchResult.results || [];
            
            // Add new results to LazyLoadManager
            if (window.lazyLoadManager && Array.isArray(newResults) && newResults.length > 0) {
                const currentFiles = window.lazyLoadManager.allFiles || [];
                const existingKeys = new Set(currentFiles.map(f => `${f.p || ''}|${f.n || ''}`));
                
                // Add only new results (not already in list)
                const uniqueNewResults = newResults.filter(item => {
                    const key = `${item.p || ''}|${item.n || ''}`;
                    return !existingKeys.has(key);
                });
                
                if (uniqueNewResults.length > 0) {
                    window.lazyLoadManager.allFiles = [...currentFiles, ...uniqueNewResults];
                    
                    if (debugMode === '1') {
                        console.log('[SEARCH] Resumed:', uniqueNewResults.length, 'new results');
                    }
                }
            }

            // Update pause state
            if (searchResult.isComplete || !searchResult.canContinue) {
                if (window.searchStateManager) {
                    window.searchStateManager._paused = false;
                }
                if (debugMode === '1') {
                    console.log('[SEARCH] Resume complete');
                }
            }
        }
    }

    /**
     * Get match conditions for current query
     */
    getMatchConditions() {
        const query = this.filterElements.searchInput ? this.filterElements.searchInput.value : '';
        if (typeof window.getMatchConditions === 'function') {
            return window.getMatchConditions(query);
        }
        return [];
    }

    updateSubcategoryOptions() {
        const { categorySelect, subcategorySelect } = this.filterElements;
        if (!subcategorySelect || !categorySelect) return;
        const selectedCategory = categorySelect.value;
        const subcategories = this.getSubcategoriesForCategory(selectedCategory);
        subcategorySelect.innerHTML = '<option value="">Все подкатегории</option>';
        subcategories.forEach(sub => {
            const option = document.createElement('option');
            option.value = sub.value;
            option.textContent = sub.label;
            subcategorySelect.appendChild(option);
        });
        this.activeFilters.subcategory = null;
    }

    getSubcategoriesForCategory(category) { return []; }

    loadSavedFilters() {
        const savedFilters = loadFilters();
        // Loading saved filters logging only in debug mode
        if (savedFilters) {
            const beforeMerge = { ...this.activeFilters };
            this.activeFilters = { ...this.activeFilters, ...savedFilters };
            // Filters merged logging only in debug mode
            this.applyFiltersToUI();
        }
        
        // Set default "Public only" filter for guests
        const isAuthenticated = !!(window.SERVER_AUTH_STATUS && window.SERVER_AUTH_STATUS.isAuthenticated);
        const isGuest = !isAuthenticated;
        const publicOnlyCheckbox = document.getElementById('public-only-filter');
        
        if (isGuest && publicOnlyCheckbox) {
            // Check URL parameter first (pu=1 or pu=0)
            let publicOnlyValue = undefined;
            try {
                const urlPu = (window.$ && typeof window.$.urlParam === 'function') ? window.$.urlParam('pu') : null;
                if (urlPu === '1' || urlPu === 1) {
                    publicOnlyValue = true;
                } else if (urlPu === '0' || urlPu === 0) {
                    publicOnlyValue = false;
                }
                // If pu parameter exists but is neither 1 nor 0, ignore it
            } catch(_) {}
            
            // If no URL parameter, check saved value
            if (publicOnlyValue === undefined) {
                const savedPublicOnly = savedFilters && savedFilters.publicOnly !== undefined ? savedFilters.publicOnly : undefined;
                if (savedPublicOnly === undefined) {
                    // No saved value and no URL parameter - set default to true for guests
                    publicOnlyValue = true;
                } else {
                    publicOnlyValue = !!savedPublicOnly;
                }
            }
            
            publicOnlyCheckbox.checked = publicOnlyValue;
            this.activeFilters.publicOnly = publicOnlyValue;
        } else if (publicOnlyCheckbox) {
            // For authenticated users, ensure publicOnly is false
            publicOnlyCheckbox.checked = false;
            this.activeFilters.publicOnly = false;
        }
    }

    applyFiltersToUI() { return applyFiltersToUI(this); }

    resetFilters() {
        this.activeFilters = { category: null, subcategory: null, format: null, search: '' };
        this.applyFiltersToUI();
        storageClear();
        this.notifyFilterChange();
    }

    matchesFilters(file) {
        if (this.activeFilters.category) {
            const fileCategory = file.category || file.cat;
            if (Array.isArray(this.activeFilters.category)) {
                if (!this.activeFilters.category.includes(fileCategory)) return false;
            } else {
                if (fileCategory !== this.activeFilters.category) return false;
            }
        }
        if (this.activeFilters.subcategory && file.subcategory !== this.activeFilters.subcategory) return false;
        if (this.activeFilters.format && file.format !== this.activeFilters.format) return false;
        if (this.activeFilters.search) {
            const searchTerm = this.activeFilters.search.toLowerCase();
            const fileName = (file.name || file.n || '').toLowerCase();
            const filePath = (file.path || file.p || '').toLowerCase();
            if (!fileName.includes(searchTerm) && !filePath.includes(searchTerm)) {
                if (this.isRegexSearch()) return this.matchesRegexPattern(file, this.activeFilters.search);
                return false;
            }
        }
        return true;
    }

    isRegexSearch() { return isRegexSearch(this.activeFilters.search || ''); }
    matchesRegexPattern(file, pattern) { return matchesRegexPattern(file, pattern); }

    onFilterChange(callback) { this.filterCallbacks.push(callback); }
    notifyFilterChange() { this.filterCallbacks.forEach(callback => { callback(this.activeFilters); }); }
    getActiveFilters() { return { ...this.activeFilters }; }
    setFilters(filters) { 
        this.activeFilters = { ...this.activeFilters, ...filters }; 
        this.applyFiltersToUI(); 
        this.notifyFilterChange(); 
        // Don't update URL here - URL will be updated when search is executed (debounce)
        this.applyFilters(false); // false = don't update URL (filter change, not search execution)
    }

    getFilterStats(files) {
        const stats = { total: files.length, filtered: 0, byCategory: {}, byFormat: {}, bySubcategory: {} };
        files.forEach(file => {
            if (this.matchesFilters(file)) {
                stats.filtered++;
                if (file.category) stats.byCategory[file.category] = (stats.byCategory[file.category] || 0) + 1;
                if (file.format) stats.byFormat[file.format] = (stats.byFormat[file.format] || 0) + 1;
                if (file.subcategory) stats.bySubcategory[file.subcategory] = (stats.bySubcategory[file.subcategory] || 0) + 1;
            }
        });
        return stats;
    }
}


